import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, MiniBoard, StatusPill, Button, ProgressBar, Text } from "components";
import {
  getDisplayStatus,
  computeCompletion,
  isComplete,
  PUBLISH_STATUS,
  DISPLAY_STATUS_AWAITING,
} from "../../lifecycle";
import Provider from "provider";
import { boardCategories, metaParts, touchedAgo, launchGame } from "../dashboardUtils";
import ActionMenu from "./ActionMenu";
import { PlayIcon, CheckIcon } from "./icons";

/**
 * Status-specific action slot (PRD §8.1): Mark-Ready for 100% drafts, a progress
 * bar for <100% drafts, a Launch mini-button for ready; nothing for played /
 * in-progress (the menu carries their actions).
 */
const CardAction = ({ game, status, onLaunch, notify }) => {
  const { percentage } = computeCompletion(game);

  if (status === DISPLAY_STATUS_AWAITING) {
    return (
      <Button
        variant="primary"
        size="md"
        startIcon={<CheckIcon size={14} />}
        disabled={!isComplete(game)}
        onClick={async (e) => {
          e.stopPropagation();
          try {
            await Provider.markGameReady(game.id);
            notify && notify("Game marked ready.", "success");
          } catch {
            notify && notify("Couldn't mark ready. Please try again.", "error");
          }
        }}
      >
        Mark as Ready
      </Button>
    );
  }

  if (status === PUBLISH_STATUS.READY) {
    return (
      <Button
        variant="cta"
        size="md"
        startIcon={<PlayIcon size={14} />}
        onClick={(e) => {
          e.stopPropagation();
          onLaunch();
        }}
      >
        Launch Game
      </Button>
    );
  }

  if (status === PUBLISH_STATUS.DRAFT) {
    return (
      <ProgressBar
        value={percentage}
        tone={percentage < 60 ? "warn" : "accent"}
        height={6}
        label={`${percentage}% complete`}
      />
    );
  }

  return null;
};

/**
 * One game in the grid (PRD §8.1). Card click → Game Home; the menu and the
 * action slot stop propagation. The generation-failed retry chip is preserved
 * from the old GameListItem as an inline error tag.
 */
const GameCard = ({ game, actions, notify }) => {
  const navigate = useNavigate();
  const status = getDisplayStatus(game);
  const { roundsLabel, cluesLabel } = metaParts(game);
  const failed = game.generation?.status === "failed";

  const goHome = () => navigate(`/manage/game/${game.id}`);
  const launch = () => launchGame(game, navigate);
  const goRetry = (e) => {
    e.stopPropagation();
    navigate(`/manage/create/ai/${game.id}/progress?retry=true`);
  };

  if (failed) {
    return (
      <Card interactive padding="none" className="da-card" onClick={goRetry}>
        <div className="da-card-thumb">
          <MiniBoard categories={boardCategories(game, 4)} values={[200, 400, 600]} />
        </div>
        <div className="da-card-body">
          <Text variant="h3" className="da-card-name">
            {game.name || "Untitled game"}
          </Text>
          <button type="button" className="da-card-failed" onClick={goRetry}>
            Generation failed — retry
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card interactive padding="none" className="da-card" onClick={goHome}>
      <div className="da-card-thumb">
        <MiniBoard categories={boardCategories(game, 4)} values={[200, 400, 600]} />
        <StatusPill status={status} size="sm" className="da-card-status" />
        <div className="da-card-menu" onClick={(e) => e.stopPropagation()}>
          <ActionMenu items={actions.menuItems} />
        </div>
      </div>
      <div className="da-card-body">
        <Text variant="h3" className="da-card-name">
          {game.name || "Untitled game"}
        </Text>
        <div className="da-card-meta">
          <span>{roundsLabel}</span>
          <span className="da-sep">·</span>
          <span>{cluesLabel}</span>
          {touchedAgo(game) && (
            <>
              <span className="da-sep">·</span>
              <span>{touchedAgo(game)}</span>
            </>
          )}
        </div>
        <div className="da-card-actionslot">
          <CardAction
            game={game}
            status={status}
            onLaunch={launch}
            notify={notify}
          />
        </div>
      </div>
    </Card>
  );
};

export default GameCard;
